import { CompulsoryItems } from '@main-application/shared/template-editor-dialog/models/template-editor-model';
import { RestTicketModel } from '@shared/interfaces/turnover.interface';
import { parseJSON } from '@shared/utils/extensions';

import { InspectionStepModel } from './inspection-step.model';
import {
  InspectionItemStatus,
  RestInspectionContentAttachment,
  RestInspectionContents,
} from './rest-inspections-model.interface';

export interface InspectionAnswer {
  title: string;
  pages: InspectionPage[];
}

export interface InspectionPage {
  id: number;
  name: string;
  title: string;
  mainImage?: InspectionImage[];
  isCompleted: boolean;
  elements?: InspectionElement[];
  uuid?: string;
}

export interface InspectionImage {
  fileUploadId: number;
  filePath: string;
  origin?: 'survey' | 'inspectionContent';
}

export interface InspectionElementItem {
  item: {
    id: string;
    text: string;
    emoji: string;
    backgroundColor: string;
    isDefault: boolean;
    showQtyWidget: boolean;
    onSelect: CompulsoryItems[];
  };
}

export interface InspectionElement {
  title: string;
  name: string;
  type: string;
  isRequired: boolean;
  items?: InspectionElementItem[];
  guid: string;
  value: string;
  choices: string[];
  visibleIf: null;
  requiredIf: null;
  elements: InspectionElement[];
}

export class InspectionAreaModel {
  public readonly content: RestInspectionContents;
  public readonly tickets: RestTicketModel[];

  steps: InspectionStepModel[];
  readonly stepsCount: number;
  readonly completedStepsCount: number;
  readonly isCompleted: boolean;
  readonly isSkipped: boolean;
  readonly title: string;
  readonly id: number;

  constructor(content: RestInspectionContents, tickets: RestTicketModel[]) {
    this.content = content;
    this.tickets = tickets;

    const answer = parseJSON<InspectionAnswer>(this.content.answerJSON);
    this.id = this.content.id;
    this.title =
      this.content.title || this.content.inspectionTemplateAreaTitle || this.content.sharedInspectionLibrarySpaceTitle;
    const attachmentsByIndex = new Map<number, InspectionImage[]>();
    const attachmentsByPageId = new Map<string, InspectionImage[]>();
    const unassignedAttachments: InspectionImage[] = [];

    (this.content.attachments ?? []).forEach(attachment => {
      const image = toInspectionImage(attachment);
      if (!image) return;

      let assigned = false;
      if (attachment.inspectionContentPageIndex !== null && attachment.inspectionContentPageIndex !== undefined) {
        const pageIndex = attachment.inspectionContentPageIndex;
        const list = attachmentsByIndex.get(pageIndex) ?? [];
        list.push(image);
        attachmentsByIndex.set(pageIndex, list);
        assigned = true;
      }
      if (attachment.pageId !== null && attachment.pageId !== undefined) {
        const key = String(attachment.pageId);
        const list = attachmentsByPageId.get(key) ?? [];
        list.push(image);
        attachmentsByPageId.set(key, list);
        assigned = true;
      }
      if (!assigned) {
        unassignedAttachments.push(image);
      }
    });

    this.steps =
      answer?.pages
        ?.map((page, idx) => {
          const surveyImages =
            page.mainImage?.map(image => ({
              ...image,
              origin: 'survey' as const,
            })) ?? [];
          const pageImages = [
            ...surveyImages,
            ...(attachmentsByIndex.get(idx) ?? []),
            ...(page.uuid ? attachmentsByPageId.get(String(page.uuid)) ?? [] : []),
            ...(page.id !== undefined && page.id !== null ? attachmentsByPageId.get(String(page.id)) ?? [] : []),
          ];

          if (idx === 0 && unassignedAttachments.length) {
            pageImages.push(...unassignedAttachments.splice(0, unassignedAttachments.length));
          }

          page.mainImage = dedupeImages(pageImages);

          return page;
        })
        .map((page, idx) => new InspectionStepModel(page, this.tickets, this.title, this.id, idx)) ?? [];
    this.stepsCount = answer?.pages?.length ?? 1;
    this.completedStepsCount = this.steps?.filter(step => step.page.isCompleted)?.length ?? 0;

    this.isCompleted = this.content.status === InspectionItemStatus.Completed;
    this.isSkipped = this.content.status === InspectionItemStatus.Skipped;

    if (this.steps?.length === 1 && this.steps[0].isCustomStep) {
      this.title = this.steps[0].page.title;
    }
  }
}

function toInspectionImage(attachment: RestInspectionContentAttachment): InspectionImage | null {
  if (!attachment?.fileUploadId) return null;

  return {
    fileUploadId: attachment.fileUploadId,
    filePath: attachment.url,
    origin: 'inspectionContent',
  };
}

function dedupeImages(images: InspectionImage[]): InspectionImage[] {
  const byId = new Map<number, InspectionImage>();

  images.forEach(image => {
    if (!image?.fileUploadId) return;

    const existing = byId.get(image.fileUploadId);
    if (!existing) {
      byId.set(image.fileUploadId, image);
      return;
    }

    if (image.origin === 'inspectionContent' && existing.origin !== 'inspectionContent') {
      byId.set(image.fileUploadId, {
        ...existing,
        ...image,
        origin: 'inspectionContent',
      });
    } else if (!existing.filePath && image.filePath) {
      byId.set(image.fileUploadId, {
        ...existing,
        filePath: image.filePath,
      });
    }
  });

  return Array.from(byId.values());
}
